import { useCallback, useEffect, useRef, useState } from 'react';
import OfferTimer, { isOfferAvailable } from '~/components/ui/OfferTimer';
import { useStore } from '@nanostores/react';
import DeployModal from '~/components/ui/DeployModal';
import { DownloadModal } from '~/components/ui/DownloadModal';
import { workbenchStore } from '~/lib/stores/workbench';
import * as Sentry from '@sentry/remix';
import HelpMenu from '~/components/ui/HelpMenu';
import { selectionStore } from '~/lib/persistence/selectionStore';
import { useTranslation } from '~/lib/hooks/useTranslation';
import { usePosthogCommonEvents } from '~/lib/hooks/posthog/usePosthogCommonEvents';
import { getFramework, getGitRepoStatus, getMessagesById, updateSupabaseData } from '~/lib/persistence/db';
import { projectId } from '~/lib/persistence';
import { db } from '~/lib/persistence';
import { ProUpgradeModal } from '~/components/ui/ProUpgradeModal';
import { userStore } from '~/lib/stores/user';
import { PullRequestModal } from '~/components/ui/PullRequestModal';
import { CreateRepoModal } from '~/components/ui/CreateRepoModal';
import GithubAuthModal from './GithubAuthModal';
import { toggleAssetListPopup } from '~/lib/stores/assets';
import { SupabaseOauthModal } from '~/components/supabase/SupabaseOAuthModal';
import {
  githubOwnerStore,
  githubRepoStore,
  githubBranchStore,
  supabaseProjectIdStore,
  supabaseProjectNameStore,
  supabaseAnonKeyStore,
  supabaseOrganizationIdStore,
  supabaseOrganizationNameStore,
  supabaseConnectionStatusStore,
  setAllSupabaseData,
  updateSupabaseConnectionStatus,
  setSupabaseConnectionStatus,
} from '~/lib/stores/integrations';
import {
  techStackSelectionStore,
  setFramework,
  setLanguage,
  type Language,
  type Framework,
} from '~/lib/stores/techStackSelection';
import { planNameCleaner } from '~/lib/utils/planNameCleaner';
import { chatState } from '~/lib/stores/chatState';
import type { ConnectionData } from '~/services/interfaceService';
import { filterFilesByGitignore } from '~/utils/gitignore';

// cloudRevert
// import { useFilteredEventStatuses, useEventStatusConnection } from '~/lib/hooks/useEventStatus';
// import { EventStatusConnectionIndicator } from '~/components/ui/EventStatusProvider';
// import { EventStatusDisplay } from '~/components/ui/EventStatusDisplay';
import IconsMapping from '~/components/ui/Icons/IconsMapping';

// Add array of frameworks where deploy should be disabled
const DEPLOY_DISABLED_FRAMEWORKS = ['angular_css', 'reactnative', 'flutter'];

// Add this interface near the top with other interfaces
interface GitHubRepoResponse {
  permissions?: {
    push?: boolean;
  };
}

// Add this near the top with other interfaces
interface GitRepoStatus {
  isGitRepo: boolean;
}

interface RightTopBarProps {
  netlifyConfig: {
    NEXT_PUBLIC_NETLIFY_CLIENT_ID: string;
    NEXT_PUBLIC_NETLIFY_APP_URL: string;
  };
  githubCreds?: {
    GITHUB_CLIENT_ID: string;
  };
  paymentUrl: string;
}

const RightTopBar = ({ netlifyConfig, githubCreds, paymentUrl }: RightTopBarProps) => {
  const { t } = useTranslation();
  const { trackGithubEvent } = usePosthogCommonEvents();
  const githubOwner = useStore(githubOwnerStore);
  const githubRepo = useStore(githubRepoStore);
  const githubBranch = useStore(githubBranchStore);
  const { status } = useStore(chatState);
  const isStreaming = status === 'streaming';
  const promptSubmited = status === 'submitted';
  const [isDeployModalOpen, setIsDeployModalOpen] = useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isProUpgradeModalOpen, setIsProUpgradeModalOpen] = useState(false);
  const [techStack, setTechStack] = useState<{ framework: string; language: string } | null>(null);
  const [dbReady, setDbReady] = useState(false);
  const userData = useStore(userStore);

  const [isCreatingPR, setIsCreatingPR] = useState(false);
  const [isPRModalOpen, setIsPRModalOpen] = useState(false);
  const [isCreateRepoModalOpen, setIsCreateRepoModalOpen] = useState(false);
  const [isGitRepo, setIsGitRepo] = useState(false);
  const [onAuthRequired, setOnAuthRequired] = useState(false);
  const [showGithubModal, setShowGithubModal] = useState(false);
  const [showSupabaseModal, setShowSupabaseModal] = useState(false);
  const [showButtonStatusTest, setShowButtonStatusTest] = useState(false);
  const loginSuccessTimeout = useRef<NodeJS.Timeout | null>(null);

  const { email, isSubscribed, promptsUsed, maxPrompts, planName } = userData;
  const areAllPromptsUsed = promptsUsed === maxPrompts;

  // Real-time event monitoring for current project
  const pId = projectId.get();

  // cloudRevert
  // const [showButtonStatusTest, setShowButtonStatusTest] = useState(false);
  // const { isConnected } = useEventStatusConnection();
  // const projectEvents = useFilteredEventStatuses({
  //   projectId: pId || undefined,
  // });
  // const pendingEventsCount = Object.values(projectEvents).filter((e) => e?.status === 'PENDING').length;
  // const failedEventsCount = Object.values(projectEvents).filter((e) => e?.status === 'FAILED').length;

  const techStackSelection = useStore(techStackSelectionStore);
  const framework = techStackSelection.framework;
  console.log('framework >>>>>>>>', framework);

  const supabaseProjectId = useStore(supabaseProjectIdStore);
  const supabaseProjectName = useStore(supabaseProjectNameStore);
  const supabaseAnonKey = useStore(supabaseAnonKeyStore);
  const supabaseOrganizationId = useStore(supabaseOrganizationIdStore);
  const supabaseOrganizationName = useStore(supabaseOrganizationNameStore);
  const supabaseConnectionStatus = useStore(supabaseConnectionStatusStore);

  // TODO: talk to @sayan
  async function setSupabaseData() {
    console.log(
      'setSupabaseData hitting >>>',
      supabaseProjectId,
      supabaseProjectName,
      supabaseAnonKey,
      supabaseOrganizationId,
      supabaseOrganizationName,
    );

    const pId = projectId.get();

    if (db && pId) {
      const chat = await getMessagesById(db, pId);
      console.log('chat from setSupabaseData >>>', chat, chat.connections);

      if (chat?.connections && chat.connections?.supabase) {
        const supabaseProjectIdDb = chat.connections?.supabase?.projectId;
        const supabaseProjectNameDb = chat.connections?.supabase?.projectName;
        const supabaseAnonKeyDb = chat.connections?.supabase?.anonKey;
        const supabaseOrganizationIdDb = chat.connections?.supabase?.organizationId;
        const supabaseOrganizationNameDb = chat.connections?.supabase?.organizationName;

        console.log('supabaseProjectId from db>>>', supabaseProjectIdDb);
        console.log('supabaseProjectName from db>>>', supabaseProjectNameDb);
        console.log('supabaseAnonKey from db>>>', supabaseAnonKeyDb);
        console.log('supabaseOrganizationId from db>>>', supabaseOrganizationIdDb);
        console.log('supabaseOrganizationName from db>>>', supabaseOrganizationNameDb);

        // Only update if we have valid Supabase data
        if (
          supabaseProjectIdDb &&
          supabaseProjectNameDb &&
          supabaseAnonKeyDb &&
          supabaseOrganizationIdDb &&
          supabaseOrganizationNameDb
        ) {
          setAllSupabaseData(
            supabaseProjectIdDb,
            supabaseProjectNameDb,
            supabaseAnonKeyDb,
            supabaseOrganizationIdDb || '',
            supabaseOrganizationNameDb || '',
          );
        } else {
          if (
            supabaseProjectId &&
            supabaseProjectName &&
            supabaseAnonKey &&
            supabaseOrganizationId &&
            supabaseOrganizationName
          ) {
            console.log('[supabase] updateSupabaseData called from rightTopBar.tsx');
            await updateSupabaseData(
              db,
              pId,
              supabaseProjectId,
              supabaseProjectName,
              supabaseAnonKey,
              supabaseOrganizationId,
              supabaseOrganizationName,
            );
          }
        }

        setAllSupabaseData(
          supabaseProjectIdDb,
          supabaseProjectNameDb,
          supabaseAnonKeyDb,
          supabaseOrganizationIdDb || '',
          supabaseOrganizationNameDb || '',
        );
      } else {
        // if supabase data does not exist in db
        if (
          supabaseProjectId &&
          supabaseProjectName &&
          supabaseAnonKey &&
          supabaseOrganizationId &&
          supabaseOrganizationName
        ) {
          console.log('[supabase] updateSupabaseData called from rightTopBar.tsx 2');
          await updateSupabaseData(
            db,
            pId,
            supabaseProjectId,
            supabaseProjectName,
            supabaseAnonKey,
            supabaseOrganizationId,
            supabaseOrganizationName,
          );

          // cloud upload
          const connectionData: ConnectionData = {
            connectionType: 'supabase',
            connectionData: {
              anonKey: supabaseAnonKey,
              organizationId: supabaseOrganizationId,
              organizationName: supabaseOrganizationName,
              projectId: supabaseProjectId,
              projectName: supabaseProjectName,
            },
          };

          console.log('[projects] projectId from Chat.client.tsx: ', projectId.get());

          // cloudRevert (burst)
          // await updateProjectConnection(pId, connectionData);
        } else {
          console.log('supabase data does not exist in db');
        }

        setAllSupabaseData(
          supabaseProjectId,
          supabaseProjectName,
          supabaseAnonKey,
          supabaseOrganizationId || '',
          supabaseOrganizationName || '',
        );
      }
    } else {
      console.log('out of setSupabaseData after if else', db, pId);
    }

    console.log(
      'ultimate supabase data >>>',
      supabaseProjectId,
      supabaseProjectName,
      supabaseAnonKey,
      supabaseOrganizationId,
      supabaseOrganizationName,
    );

    updateSupabaseConnectionStatus();
  }

  useEffect(() => {
    if (pId && db && supabaseProjectId && supabaseAnonKey) {
      console.log('[supabase] calling setSupabaseData from rightTopBar.tsx >>>', pId);
      setSupabaseData();
    }
  }, [db, pId, supabaseProjectId, supabaseAnonKey]);

  if (!framework) {
    return null;
  }

  // Determine if buttons should be disabled
  const isDeployDisabled =
    isStreaming || promptSubmited || DEPLOY_DISABLED_FRAMEWORKS.includes(framework.toLowerCase());
  const isDownloadDisabled = isStreaming || promptSubmited;

  const handleModalConfirm = () => {
    onDownloadClick();
    setIsDownloadModalOpen(false);
  };

  const confirmDownload = useCallback(() => {
    console.log('Download confirmed - starting download process');

    try {
      workbenchStore.downloadZip();
      console.log('Download initiated successfully');
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          message: 'Error during download',
          framework,
        },
      });
      console.error('Download failed:', error);
    }
    setIsDownloadModalOpen(false);
  }, []);

  const onDownloadClick = useCallback(() => {
    console.log('Download button clicked - opening modal');
    confirmDownload();
  }, []);

  useEffect(() => {
    // Check if IndexedDB is ready
    if (db) {
      setDbReady(true);
    }
  }, []);

  // Initial connection status check
  useEffect(() => {
    updateSupabaseConnectionStatus();
  }, []);

  // Check for existing Supabase connection
  useEffect(() => {
    updateSupabaseConnectionStatus();
  }, [showSupabaseModal]);

  useEffect(() => {
    const getCurrentTechStack = async () => {
      const currentProjectId = projectId.get();
      console.log('getCurrentTechStack called - projectId:', currentProjectId);
      console.log('Current dbReady state:', dbReady);
      console.log('Current db instance:', db);

      if (!db || !currentProjectId) {
        console.log('Exiting getCurrentTechStack - missing db or chatId');

        const framework = techStackSelection.framework;
        const language = techStackSelection.language;
        console.log('Setting techStack from selectionStore (no DB result):', { framework, language });
        setFramework(framework);
        setLanguage(language);
        setTechStack({ framework, language });
        return;
      }

      try {
        const result = await getFramework(db, currentProjectId);
        console.log('Framework result from DB:', result);

        if (result) {
          const { framework, language } = result;
          console.log('Framework/language from DB:', { framework, language });

          if (framework && language && framework !== 'unknown' && language !== 'unknown') {
            console.log('Setting techStack from DB values:', { framework, language });
            setFramework(framework as Framework);
            setLanguage(language as Language);
            setTechStack({ framework, language });
          } else {
            const framework = techStackSelection.framework;
            const language = techStackSelection.language;
            console.log('Setting techStack from selectionStore (DB values unknown):', { framework, language });
            setFramework(framework as Framework);
            setLanguage(language as Language);
            setTechStack({ framework, language });
          }
        } else {
          const framework = techStackSelection.framework;
          const language = techStackSelection.language;
          console.log('Setting techStack from selectionStore (no DB result):', { framework, language });
          setFramework(framework as Framework);
          setLanguage(language as Language);
          setTechStack({ framework, language });
        }
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            message: 'Error getting framework',
          },
        });
        console.error('Error in getCurrentTechStack:', error);

        // Fallback to selection store
        const framework = techStackSelection.framework;
        const language = techStackSelection.language;
        console.log('Setting techStack from selectionStore (error fallback):', { framework, language });
        setFramework(framework as Framework);
        setLanguage(language as Language);
        setTechStack({ framework, language });
      }
    };
    getCurrentTechStack();
  }, [dbReady, projectId.get()]);

  // Add debug logging for techStack changes
  useEffect(() => {
    console.log('Current techStack state:', techStack);
  }, [techStack]);

  // Add this new useEffect to check git repo status
  const checkGitRepoStatus = async () => {
    const currentProjectId = await projectId.get();
    console.log('[checkGitRepoStatus] Current projectId:', currentProjectId);

    // if (!currentProjectId) {

    const token = selectionStore.get().githubAccessToken;
    const owner = githubOwnerStore.get();
    const gitRepoName = githubRepoStore.get();
    console.log('>>>[checkGitRepoStatus] owner:', owner);
    console.log('>>>[checkGitRepoStatus] gitRepoName:', gitRepoName);
    console.log('>>>[checkGitRepoStatus] token:', token);

    // Check if we have the required GitHub info
    if (!owner || !gitRepoName || !token) {
      console.log('[checkGitRepoStatus] Missing GitHub info, setting isGitRepo to false');
      setIsGitRepo(false);
      return false;
    }

    try {
      // Validate the repository exists and is accessible
      const response = await fetch(`https://api.github.com/repos/${owner}/${gitRepoName}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/vnd.github+json',
        },
      });

      if (response.ok) {
        console.log('[checkGitRepoStatus] Valid GitHub repo found');
        setIsGitRepo(true);
        return true;
      } else {
        console.log('[checkGitRepoStatus] Invalid or inaccessible GitHub repo');
        setIsGitRepo(false);
        return false;
      }
    } catch (error) {
      console.error('[checkGitRepoStatus] Error checking git repo status:', error);
      setIsGitRepo(false);
      return false;
    }

    // }
    // else {
    //   try {
    //     const result = await getGitRepoStatus(currentProjectId);
    //     console.log('[checkGitRepoStatus] Git repo status result:', result);
    //     setIsGitRepo(result?.isGitRepo ?? false);
    //     return result?.isGitRepo ?? false;
    //   } catch (error) {
    //     console.error('[checkGitRepoStatus] Error checking git repo status:', error);
    //     setIsGitRepo(false);
    //     return false;
    //   }
    // }
    return false;
  };

  useEffect(() => {
    checkGitRepoStatus();
  }, [dbReady, githubOwner, githubRepo]);

  const getTrackedFiles = async (): Promise<Set<string>> => {
    const token = selectionStore.get().githubAccessToken;
    const repo = githubRepo;
    const owner = githubOwner;

    try {
      const response = await fetch('/api/github', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'getTrackedFiles',
          repo: `${owner}/${repo}`,
          branch: githubBranch,
        }),
      });

      if (!response.ok) {
        return new Set<string>();
      }

      const data = (await response.json()) as { files: string[] };
      return new Set(data.files);
    } catch (error) {
      return new Set<string>();
    }
  };

  const handleCreateCommit = async (targetBranch: string, commitMessage: string): Promise<string> => {
    try {
      setIsCreatingPR(true);

      const token = selectionStore.get().githubAccessToken;
      const repo = githubRepo;
      const owner = githubOwner;

      // Track GitHub push started
      try {
        trackGithubEvent('push_started', {
          target_branch: targetBranch,
          repo,
          owner,
        });
      } catch (error) {
        console.error('[PostHog] Failed to track github_push_started:', error);
      }

      // Check permissions
      const permissionsResponse = await fetch(`https://api.github.com/repos/${owner}/${repo}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/vnd.github+json',
        },
      });

      const repoData = (await permissionsResponse.json()) as GitHubRepoResponse;

      if (!permissionsResponse.ok) {
        throw new Error('Repository not found or no access');
      }

      if (!repoData.permissions?.push) {
        throw new Error("You don't have write access to this repository");
      }

      // Prepare files
      const allFiles = await workbenchStore.getAllFiles();
      const allFilesMap = await workbenchStore.getAllFilesMap();
      const gitignoreEntry = Object.entries(allFilesMap).find(([path]) => path.endsWith('.gitignore'));
      const gitignoreContent = gitignoreEntry && gitignoreEntry[1]?.type === 'file' ? gitignoreEntry[1].content : '';
      const trackedFiles = await getTrackedFiles();
      const files = filterFilesByGitignore(allFiles, gitignoreContent, trackedFiles);
      console.log('>>>[handleCreateCommit] githubBranch: ', githubBranch, 'targetBranch: ', targetBranch);

      // Create PR
      const response = await fetch('/api/github', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'createCommit',
          repo: `${owner}/${repo}`,
          files,
          targetBranch,
          sourceBranch: githubBranch,
          commitMessage,
        }),
      });

      if (!response.ok) {
        const errorData = (await response.json()) as { error: string };
        console.log('>>>[handleConfirmPR] errorData: ', errorData);
        throw new Error(errorData.error || 'Failed to create PR');
      }

      const data = (await response.json()) as { type: string; message: string };
      console.log('>>>[handleConfirmPR] data: ', data.type);

      setIsCreatingPR(false);

      // Track GitHub push completed (success)
      if (data.type === 'success') {
        try {
          trackGithubEvent('push_completed', {
            target_branch: targetBranch,
            repo,
            owner,
            status: 'success',
          });
        } catch (error) {
          console.error('[PostHog] Failed to track github_push_completed:', error);
        }
      }

      return data.type;
    } catch (error) {
      setIsCreatingPR(false);

      // Track GitHub push failed
      try {
        trackGithubEvent('push_failed', {
          target_branch: targetBranch,
          repo: githubRepo,
          owner: githubOwner,
          error: error instanceof Error ? error.message : 'Unknown error',
        });
      } catch (e) {
        console.error('[PostHog] Failed to track github_push_failed:', e);
      }

      // Keep modal open on error
      throw error;
    }
  };

  const handleGitHubAction = async (e: React.MouseEvent) => {
    e.preventDefault();

    // Check if user is authenticated
    const token = selectionStore.get().githubAccessToken;

    if (!token) {
      // Call the callback to trigger auth flow
      setOnAuthRequired(true);
      setShowGithubModal(true);
      return;
    }

    const isGitRepo = await checkGitRepoStatus();
    console.log('>>>[handleGitHubAction] isGitRepo: ', isGitRepo);

    if (isGitRepo) {
      // Reset PR modal state before opening
      setIsPRModalOpen(false);
      setIsPRModalOpen(true);
    } else {
      // Reset Create Repo modal state before opening
      setIsCreateRepoModalOpen(false);
      setIsCreateRepoModalOpen(true);
    }
  };

  // Handle global GitHub auth signals
  useEffect(() => {
    const handleLoginRequired = () => {
      setOnAuthRequired(true);
      setShowGithubModal(true);
      setIsCreateRepoModalOpen(false);
      setIsPRModalOpen(false);
    };

    const handleLoginSuccess = () => {
      setOnAuthRequired(false);

      if (loginSuccessTimeout.current) {
        clearTimeout(loginSuccessTimeout.current);
      }

      loginSuccessTimeout.current = setTimeout(async () => {
        const repoConnected = await checkGitRepoStatus();

        // Close auth modal when transitioning
        setShowGithubModal(false);

        if (repoConnected) {
          setIsPRModalOpen(true);
        } else {
          setIsCreateRepoModalOpen(true);
        }
      }, 2400);
    };

    window.addEventListener('github:login:required', handleLoginRequired);
    window.addEventListener('github:login:success', handleLoginSuccess);

    return () => {
      window.removeEventListener('github:login:required', handleLoginRequired);
      window.removeEventListener('github:login:success', handleLoginSuccess);

      if (loginSuccessTimeout.current) {
        clearTimeout(loginSuccessTimeout.current);
      }
    };
  }, []);

  return (
    <>
      <div className="flex items-center justify-center gap-2 mr-4 text-dualite-text-primary">
        {/* {techStack && (
          <div className="text-gray-200 flex flex-row gap-1 text-[7px]">
            <p className="flex items-center bg-alpha-background-editor-button rounded-md">
              <span className="px-1">
                {techStack?.framework ? FRAMEWORK_DISPLAY_NAMES[techStack.framework] || techStack.framework : ''}
              </span>
            </p>
            <p className="flex items-center bg-alpha-background-editor-button rounded-md">
              <span className="px-1">{techStack?.language?.toUpperCase()}</span>
            </p>
          </div>
        )} */}
        {/* Assets button */}
        <button
          className="flex items-center justify-center w-[35px] h-[35px] rounded-md text-dualite-text-primary bg-dualite-background-button hover:bg-dualite-background-button-hover transition-colors duration-150"
          onClick={() => toggleAssetListPopup()}
          title="Project Assets"
        >
          <div className="i-ph:database-bold text-[15px]" />
        </button>
        {/* Supabase Button */}
        <div className="relative">
          <button
            className={`flex items-center justify-center gap-1.5 h-[35px] rounded-md px-3 text-dualite-text-primary transition-colors duration-150 bg-dualite-background-button ${isStreaming ? 'opacity-50 cursor-not-allowed' : 'hover:bg-dualite-background-button-hover'}`}
            onClick={() => setShowSupabaseModal(true)}
            disabled={isStreaming}
            title={
              isStreaming
                ? t('header.supabaseDisabled')
                : supabaseConnectionStatus.isConnected && projectId.get()
                  ? t('header.supabaseReadOnly')
                  : supabaseConnectionStatus.isConnected
                    ? t('header.supabaseConnected')
                    : t('header.supabaseDatabase')
            }
          >
            {IconsMapping.supabaseIcon}
            {supabaseConnectionStatus.isConnected && <div className="i-ph:check-bold text-[10px] text-emerald-400" />}
            {supabaseConnectionStatus.isDisconnected && (
              <div className="i-ph:warning-bold text-[10px] text-orange-400" />
            )}
          </button>
        </div>
        {/* GitHub Button */}
        <div className="relative">
          <button
            className={`flex items-center justify-center gap-1.5 px-3 h-[35px] rounded-md text-dualite-text-primary transition-colors duration-150 bg-dualite-background-button ${isStreaming || isCreatingPR ? 'opacity-50 cursor-not-allowed' : 'hover:bg-dualite-background-button-hover'}`}
            onClick={handleGitHubAction}
            disabled={isStreaming || isCreatingPR}
            title={
              isStreaming
                ? t('header.githubDisabled')
                : isGitRepo
                  ? t('header.githubConnected')
                  : t('header.githubOptions')
            }
          >
            {IconsMapping.githubIcon}
            {isGitRepo && <div className="i-ph:check-bold text-[10px] text-emerald-400" />}
          </button>
        </div>
        {/* Download Button */}
        <div
          onClick={() => !isDownloadDisabled && setIsDownloadModalOpen(true)}
          className={`flex items-center justify-center w-[35px] h-[35px] rounded-md text-dualite-text-primary bg-dualite-background-button transition-colors duration-150 ${isDownloadDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-dualite-background-button-hover'}`}
          title={isDownloadDisabled ? t('header.downloadDisabled') : t('header.downloadZip')}
        >
          <div className="i-ph:download-simple-bold text-[15px]" />
        </div>
        {/* Help Button */}
        <div className="z-50">
          <HelpMenu
            position="flex"
            framework={framework}
            withBg
            buttonClassName="flex items-center justify-center w-[35px] h-[35px] rounded-md bg-dualite-background-button text-dualite-text-primary hover:bg-dualite-background-button-hover transition-colors duration-150"
          />
        </div>
        {/* Plan / Upgrade Button */}
        {email !== 'user_email@dummy.dualite' &&
          (isSubscribed && areAllPromptsUsed ? (
            <button
              className="flex items-center justify-center gap-2 h-[35px] rounded-md px-3 text-xs font-medium text-dualite-text-primary bg-dualite-background-button hover:bg-dualite-background-button-hover transition-colors duration-150 whitespace-nowrap"
              onClick={() => setIsProUpgradeModalOpen(true)}
              title={t('header.quotaExhausted')}
            >
              {t('header.exhausted')}
            </button>
          ) : isSubscribed ? (
            <button
              className="flex items-center justify-center gap-2 h-[35px] rounded-md px-3 text-xs font-medium bg-dualite-background-button text-dualite-text-primary hover:bg-dualite-background-button-hover transition-colors duration-150 whitespace-nowrap"
              onClick={() => alert(t('header.usageDashboard'))}
              title={t('header.proAccount')}
            >
              {planNameCleaner(planName).planName}
            </button>
          ) : (
            <button
              className="flex items-center justify-center gap-2 h-[35px] rounded-md px-3 text-xs font-medium bg-dualite-background-cta text-dualite-text-cta hover:bg-dualite-background-cta-hover transition-colors duration-150 whitespace-nowrap"
              onClick={() => setIsProUpgradeModalOpen(true)}
              title={t('header.upgradeToPro')}
            >
              {t('header.upgrade')}
              {isOfferAvailable() && <OfferTimer mini />}
            </button>
          ))}
        {/* Publish Button */}
        <div className="relative">
          <button
            className={`flex items-center justify-center gap-2 h-[35px] rounded-md px-4 text-white text-xs font-semibold transition-colors duration-150 ${isDeployDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            style={{
              background: isDeployDisabled
                ? 'linear-gradient(0deg, var(--dualite-primary-end) 0%, var(--dualite-primary-start) 100%)'
                : 'linear-gradient(0deg, var(--dualite-primary-end) 0%, var(--dualite-primary-start) 100%)',
              opacity: isDeployDisabled ? 0.6 : 1,
            }}
            onClick={() => !isDeployDisabled && setIsDeployModalOpen(!isDeployModalOpen)}
            disabled={isDeployDisabled || isDeployModalOpen}
            title={
              isStreaming
                ? t('header.deployDisabled')
                : isDeployDisabled
                  ? t('header.deployUnavailable')
                  : t('header.deployInstantly')
            }
          >
            {t('header.publish')}
          </button>
          <div className="absolute top-full right-0 mt-2 z-50">
            <DeployModal
              isOpen={isDeployModalOpen}
              onClose={() => setIsDeployModalOpen(false)}
              netlifyConfig={netlifyConfig}
            />
          </div>
        </div>
        {/* Real-time Event Status Indicator */}
        {/* {(pendingEventsCount > 0 || failedEventsCount > 0) && process.env.NODE_ENV === 'development' && (
          <div
            className="flex items-center gap-1 rounded-full px-2 py-1 border border-alpha-background-editor-button"
            title={`Events: ${pendingEventsCount} pending, ${failedEventsCount} failed`}
          >
            {pendingEventsCount > 0 && (
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse" />
                <span className="text-xs text-yellow-500">{pendingEventsCount}</span>
              </div>
            )}
            {failedEventsCount > 0 && (
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-red-500" />
                <span className="text-xs text-red-500">{failedEventsCount}</span>
              </div>
            )}
          </div>
        )} */}
        {/* Event Stream Connection Status (Development) */}
        {/* {process.env.NODE_ENV === 'development' && (
          <div className="border-l border-alpha-background-editor-button pl-2">
            <EventStatusConnectionIndicator />
          </div>
        )} */}
        {/* Button Status Test Toggle (Development Only) */}
        {/* <button
          onClick={() => setShowButtonStatusTest(!showButtonStatusTest)}
          className={`flex justify-center items-center gap-2 rounded-full px-2 py-2 border transition-colors ${isConnected ? 'border-green-500 hover:bg-green-500/10' : 'border-red-500 hover:bg-red-500/10'}`}
          title={isConnected ? 'Event stream: Connected' : 'Event stream: Disconnected'}
        >
          <div className={`${isConnected ? 'i-ph:cloud-check text-green-500' : 'i-ph:cloud-x text-red-500'} text-sm`} />
        </button> */}
      </div>
      <ProUpgradeModal
        isOpen={isProUpgradeModalOpen}
        onClose={() => setIsProUpgradeModalOpen(false)}
        isTopUp={isSubscribed && areAllPromptsUsed}
        paymentUrl={paymentUrl}
      />
      <DownloadModal
        isOpen={isDownloadModalOpen}
        onClose={() => setIsDownloadModalOpen(false)}
        onConfirm={handleModalConfirm}
      />
      <PullRequestModal
        isOpen={isPRModalOpen}
        onClose={() => setIsPRModalOpen(false)}
        onConfirm={handleCreateCommit}
        setIsPRModalOpen={setIsPRModalOpen}
        setIsGitRepo={setIsGitRepo}
      />
      <CreateRepoModal
        isOpen={isCreateRepoModalOpen}
        onClose={() => setIsCreateRepoModalOpen(false)}
        setIsGitRepo={setIsGitRepo}
      />
      {(showGithubModal || onAuthRequired) && githubCreds && (
        <GithubAuthModal
          isOpen={showGithubModal}
          onClose={() => {
            setShowGithubModal(false);
          }}
          githubCreds={githubCreds}
          requireAuth={true}
        />
      )}
      {/* Supabase Modal */}
      <SupabaseOauthModal
        isOpen={showSupabaseModal}
        onClose={() => setShowSupabaseModal(false)}
        onSuccess={(credentials) => {
          console.log('Supabase connection successful:', credentials);
          setShowSupabaseModal(false);
        }}
        onError={(error) => {
          console.error('Supabase connection failed:', error);
          setShowSupabaseModal(false);
        }}
        currentProjectId={supabaseProjectId}
        currentProjectName={supabaseProjectName}
        currentOrganizationId={supabaseOrganizationId}
        currentOrganizationName={supabaseOrganizationName}
      />
      {/* Event Status Display Modal */}
      {/* cloudRevert */}
      {/* {showButtonStatusTest && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="relative max-h-[90vh] w-full max-w-6xl overflow-y-auto rounded-lg bg-dualite-background-primary shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-dualite-border-primary bg-dualite-background-secondary px-6 py-4">
              <h2 className="text-xl font-bold text-white">Event Status Monitor (Real-time)</h2>
              <button
                onClick={() => setShowButtonStatusTest(false)}
                className="rounded-full p-2 text-white hover:bg-alpha-background-editor-button transition-colors"
                title="Close"
              >
                <div className="i-ph:x text-xl" />
              </button>
            </div>
            <div className="p-6">
              <EventStatusDisplay projectId={pId || undefined} />
            </div>
          </div>
        </div>
      )} */}
    </>
  );
};

export default RightTopBar;
